import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../api/apiClient';
import LoadingState from '../components/LoadingState';
import { getUserFriendlyError } from '../utils/apiErrors';

const SupportTicketDetailPage = () => {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);

  const fetchTicketDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/support-tickets/${ticketId}`);
      setTicket(response.ticket);
      setMessages(response.messages);
    } catch (err) {
      if (err.status === 404) {
        setError('We could not find this ticket or you do not have permission to view it.');
      } else {
        setError(getUserFriendlyError(err, 'We could not load this ticket. Please try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketDetails();
  }, [ticketId]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !attachments.length) return;

    setActionError(null);
    setIsSending(true);
    try {
      const uploaded = [];
      for (const file of attachments) {
        const form = new FormData();
        form.append('ticketId', ticketId);
        form.append('file', file);
        const result = await apiClient.post('/support-attachments', form);
        uploaded.push(result.attachment);
      }
      const response = await apiClient.post('/support-tickets/' + ticketId + '/messages', {
        message: newMessage || 'Attachments added.',
        attachments: uploaded
      });
      setMessages([...messages, response]);
      setNewMessage('');
      setAttachments([]);
    } catch (err) {
      setActionError(getUserFriendlyError(err, 'Your reply could not be sent. Please try again.'));
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return <LoadingState spinnerOnly />;
  }

  if (error) {
    return <div className="mx-auto max-w-2xl p-6 text-center" role="alert"><p className="text-red-600">{error}</p><button type="button" onClick={fetchTicketDetails} className="mt-4 rounded-full bg-primary px-5 py-2 text-white">Try again</button></div>;
  }

  if (!ticket) {
    return <p>Ticket not found.</p>;
  }

  return (
    <div className="container mx-auto px-4 sm:px-8 py-8">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-semibold">{ticket.subject}</h1>
            <p className="text-sm text-gray-600">Ticket #{ticket.ticketNumber}</p>
          </div>
          <span className={`px-3 py-1 text-sm rounded-full ${
            ticket.status === 'Open' ? 'bg-green-200 text-green-800' :
            ticket.status === 'In Progress' ? 'bg-yellow-200 text-yellow-800' :
            'bg-gray-200 text-gray-800'
          }`}>
            {ticket.status}
          </span>
        </div>

        <div className="border-t border-gray-200 mt-4 pt-4 space-y-4">
          {messages.map((msg) => (
            <div key={msg._id} className={`flex ${msg.isFromAdmin ? 'justify-start' : 'justify-end'}`}>
              <div className={`p-4 rounded-lg max-w-lg ${msg.isFromAdmin ? 'bg-gray-200' : 'bg-primary text-white'}`}>
                <p className="font-bold">{msg.isFromAdmin ? 'Admin' : 'You'}</p>
                <p>{msg.message}</p>
                {Array.isArray(msg.attachments) && msg.attachments.length > 0 && <div className="mt-3 space-y-2">
                  {msg.attachments.map((file) => <a key={file.key} href={file.url} target="_blank" rel="noreferrer" className="block underline break-all text-sm">
                    {file.type && file.type.startsWith('image/') ? 'Image: ' : 'File: '}{file.name}
                  </a>)}
                </div>}
                <p className="text-xs mt-2 opacity-75">{new Date(msg.createdAt).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          {actionError && <p role="alert" className="mb-3 text-sm text-red-600">{actionError}</p>}
          <form onSubmit={handleSendMessage}>
            <textarea
              rows="4"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="w-full p-2 border border-gray-300 rounded-md"
            ></textarea>
            <label htmlFor="ticket-reply-attachments" className="block mt-3 text-sm font-medium text-gray-700">Attach images or PDF (up to 5 files, 10 MB each)</label>
            <input id="ticket-reply-attachments" type="file" multiple accept="image/jpeg,image/png,image/gif,image/webp,application/pdf" onChange={(e) => setAttachments(Array.from(e.target.files || []).slice(0, 5))} className="mt-1 block w-full text-sm" />
            {attachments.length > 0 && <p className="text-sm text-gray-500 mt-1">{attachments.map(file => file.name).join(', ')}</p>}
            <button
              type="submit"
              disabled={isSending}
              className="mt-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-60"
            >
              {isSending ? "Sending..." : "Send"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetailPage;
